import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
} from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  EmptyState,
  PageHeaderBand,
  SearchInput,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import {
  priceChangesCsvUrl,
  useAcceptSuggestedRows,
  useApplyIntake,
  useIntakeDetail,
  usePatchIntakeLine,
  useRejectIntake,
  useRetryIntake,
  useSetIntakeEffectiveDate,
  useSetIntakePriceUnit,
  useUnscheduleLines,
  useVendorPricelistItems,
} from '../api/queries'
import type { VendorPricelistIntakeLineWire as Line } from '../api/types'
import { DroppedTiersCard } from '../components/DroppedTiersCard'
import { PriceSheetLineResolver } from '../components/PriceSheetLineResolver'
import {
  GROUP_LABEL,
  isApplicable,
  lineShortReason,
  lineStatusView,
  matchesSearch,
  money,
  needsPriceUnit,
  reviewGroup,
  type ReviewGroup,
} from '../utils/price-sheet-review'

type GroupFilter = ReviewGroup | 'all'
const FILTERS: GroupFilter[] = ['all', 'decide', 'ready', 'new_tier', 'not_in_odoo', 'done']

/** Today as YYYY-MM-DD in the browser's local time (SPI is in Central). */
function localTodayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function perUnit(unit: string | null | undefined): string {
  return unit ? `/${unit}` : ''
}

/** Stops a click on a control inside a row from toggling the row open. */
function Stop({ children }: { children: React.ReactNode }) {
  return <span onClick={(e) => e.stopPropagation()}>{children}</span>
}

/**
 * Full-page review of one imported vendor price sheet: every line with its
 * Odoo match, filterable by what it needs (decision / ready / new tier / not
 * in Odoo / done), searchable, sortable, and resolvable in place. Replaces
 * the review pane that used to live inside the import drawer (a 417-line
 * Givaudan sheet in a drawer, with no way to act on held lines).
 */
export function VendorPriceSheetReviewPage() {
  const params = useParams({ strict: false }) as { vendorId?: string; intakeId?: string }
  const vendorId = Number(params.vendorId)
  const intakeId = params.intakeId ?? null
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data, isLoading, isError, error, refetch } = useIntakeDetail(intakeId)
  const items = useVendorPricelistItems(Number.isFinite(vendorId) ? vendorId : null)
  const patchLine = usePatchIntakeLine()
  const acceptSuggested = useAcceptSuggestedRows()
  const apply = useApplyIntake()
  const unschedule = useUnscheduleLines()
  const reject = useRejectIntake()
  const retry = useRetryIntake()
  const setPriceUnit = useSetIntakePriceUnit()
  const setEffectiveDate = useSetIntakeEffectiveDate()

  const [filter, setFilter] = useState<GroupFilter>('all')
  const [search, setSearch] = useState('')
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [rejectReason, setRejectReason] = useState('')
  const [showReject, setShowReject] = useState(false)

  const lines = useMemo<Line[]>(() => data?.lines ?? [], [data])
  const vendorName = data?.intake.vendor_name ?? 'Vendor'
  const vendorItems = useMemo(() => items.data?.items ?? [], [items.data])

  const counts = useMemo(() => {
    const c: Record<GroupFilter, number> = {
      all: lines.length,
      ready: 0,
      decide: 0,
      new_tier: 0,
      not_in_odoo: 0,
      done: 0,
    }
    for (const l of lines) c[reviewGroup(l)] += 1
    return c
  }, [lines])

  const claimedBy = useMemo(() => {
    const m = new Map<number, number>()
    for (const l of lines) {
      if (!['matched', 'applied', 'scheduled'].includes(l.line_status)) continue
      for (const id of l.matched_supplierinfo_ids ??
        (l.matched_supplierinfo_id ? [l.matched_supplierinfo_id] : [])) {
        m.set(id, l.line_no)
      }
    }
    return m
  }, [lines])

  const visible = useMemo(
    () =>
      lines.filter(
        (l) => (filter === 'all' || reviewGroup(l) === filter) && matchesSearch(l, search),
      ),
    [lines, filter, search],
  )

  const byId = useMemo(() => new Map(lines.map((l) => [String(l.id), l])), [lines])
  const selectedLines = Object.keys(selection)
    .filter((k) => selection[k])
    .map((k) => byId.get(k))
    .filter((l): l is Line => l != null)
  const selApplicable = selectedLines.filter(isApplicable)
  const selConfirmable = selectedLines.filter(
    (l) => l.line_status === 'unmatched' && (l.suggested_supplierinfo_ids?.length ?? 0) > 0,
  )
  const selSkippable = selectedLines.filter(
    (l) => !['applied', 'scheduled', 'skipped'].includes(l.line_status),
  )
  const selScheduled = selectedLines.filter((l) => l.line_status === 'scheduled')

  const hasSelection = selectedLines.length > 0
  useEffect(() => {
    if (!hasSelection) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      setSelection({})
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [hasSelection])

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const columns = useMemo<ColumnDef<Line, unknown>[]>(
    () => [
      {
        id: '__select',
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all shown lines"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Stop>
            <Checkbox
              aria-label={`Select line ${row.original.line_no}`}
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </Stop>
        ),
        enableSorting: false,
      },
      {
        id: 'line_no',
        header: '#',
        accessorKey: 'line_no',
        meta: { numeric: true },
        cell: (ctx) => (
          <span className="inline-flex items-center gap-1 text-tertiary">
            {expanded.has(String(ctx.row.original.id)) ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )}
            {ctx.getValue() as number}
          </span>
        ),
      },
      {
        id: 'vendor_product',
        header: 'Vendor product',
        accessorFn: (l) => l.vendor_product_name_raw ?? '',
        cell: ({ row: { original: l } }) => (
          <div>
            <div className="text-primary">{l.vendor_product_name_raw}</div>
            {l.vendor_product_code_raw ? (
              <div className="text-xs text-tertiary">{l.vendor_product_code_raw}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'spi_product',
        header: 'SPI product',
        accessorFn: (l) => l.matched_product_name ?? '',
        cell: ({ row: { original: l } }) => (
          <div className="text-secondary">
            {l.matched_product_name ?? '—'}
            {(l.matched_supplierinfo_ids?.length ?? 0) > 1 ? (
              <div className="text-xs text-tertiary">
                updates {l.matched_supplierinfo_ids!.length} Odoo rows at the same price
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'tier',
        header: 'Pack / MOQ',
        accessorFn: (l) => l.parsed_min_qty ?? l.parsed_qty ?? 0,
        cell: ({ row: { original: l } }) => (
          <div className="text-secondary">
            {l.parsed_packaging_raw ?? '—'}
            {l.parsed_min_qty != null ? (
              <div className="text-xs text-tertiary">
                MOQ {l.parsed_min_qty.toLocaleString()}
                {l.parsed_min_qty_unit ? ` ${l.parsed_min_qty_unit}` : ''}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'current',
        header: 'Odoo now',
        accessorFn: (l) => l.current_price ?? -1,
        meta: { numeric: true },
        cell: ({ row: { original: l } }) => (
          <span className="text-secondary">
            {l.current_price != null ? `${money(l.current_price)}${perUnit(l.odoo_uom)}` : '—'}
          </span>
        ),
      },
      {
        id: 'previous',
        header: 'Vendor was',
        accessorFn: (l) => l.parsed_previous_price ?? -1,
        meta: { numeric: true },
        cell: ({ row: { original: l } }) => (
          <span
            className={l.odoo_matches_previous === false ? 'text-warning-text' : 'text-tertiary'}
            title={
              l.odoo_matches_previous === false
                ? "Odoo's price isn't what the vendor says the previous price was"
                : undefined
            }
          >
            {l.parsed_previous_price != null
              ? `${l.odoo_matches_previous === false ? '≠ ' : ''}${money(l.parsed_previous_price)}${perUnit(l.parsed_price_unit)}`
              : '—'}
          </span>
        ),
      },
      {
        id: 'new',
        header: 'New',
        accessorFn: (l) => l.apply_price ?? l.parsed_price ?? -1,
        meta: { numeric: true },
        cell: ({ row: { original: l } }) =>
          l.apply_price != null ? (
            <div className="font-medium text-primary">
              {money(l.apply_price)}
              {perUnit(l.odoo_uom)}
              {l.parsed_price_unit != null && l.parsed_price_unit !== l.odoo_uom ? (
                <div className="text-xs font-normal text-tertiary">
                  {money(l.parsed_price)}
                  {perUnit(l.parsed_price_unit)} on sheet
                </div>
              ) : null}
            </div>
          ) : (
            <span className="text-tertiary">
              {money(l.parsed_price)}
              {l.parsed_price_unit ? perUnit(l.parsed_price_unit) : '/unit?'}
            </span>
          ),
      },
      {
        id: 'change',
        header: 'Change',
        accessorFn: (l) => l.change_pct ?? -9999,
        meta: { numeric: true },
        cell: ({ row: { original: l } }) =>
          l.change_pct == null ? (
            <span className="text-tertiary">—</span>
          ) : l.price_changed === false ? (
            <span className="text-tertiary">No change</span>
          ) : (
            <span className={l.change_pct > 0 ? 'text-danger' : 'text-success'}>
              {l.change_pct > 0 ? '+' : ''}
              {l.change_pct.toFixed(1)}%
            </span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (l) => lineStatusView(l).label,
        cell: ({ row: { original: l } }) => {
          const view = lineStatusView(l)
          const why = lineShortReason(l)
          return (
            <div className="flex max-w-[22rem] flex-col gap-0.5">
              <span>
                <Chip size="xs" variant={view.tone}>
                  {view.label}
                </Chip>
              </span>
              {why ? <span className="text-xs whitespace-normal text-tertiary">{why}</span> : null}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original: l } }) => {
          const group = reviewGroup(l)
          if (l.line_status === 'applied' || l.line_status === 'scheduled') return null
          return (
            <Button
              variant={group === 'decide' ? 'primary' : 'ghost'}
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                toggleExpanded(String(l.id))
              }}
            >
              {group === 'decide' ? 'Resolve' : group === 'not_in_odoo' ? 'Map' : 'Change'}
            </Button>
          )
        },
      },
    ],
    [expanded],
  )

  if (!intakeId || isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-tertiary">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="p-6">
        <EmptyState
          variant="error"
          title="Couldn't load this import"
          description={error instanceof Error ? error.message : 'Try again.'}
        />
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  const { intake } = data
  const status = intake.status
  const backToVendor = () =>
    navigate({ to: '/vendor-pricelists', search: { vendor: String(vendorId) } as never })

  const header = (
    <PageHeaderBand
      title={`Price sheet — ${vendorName}`}
      description={`${lines.length} line(s) extracted${intake.created_at ? ` · imported ${new Date(intake.created_at).toLocaleString()}` : ''}`}
    >
      <Button variant="ghost" size="sm" onClick={backToVendor}>
        <ArrowLeft size={12} /> Back to {vendorName}
      </Button>
    </PageHeaderBand>
  )

  if (
    status === 'pending_extraction' ||
    status === 'extraction_running' ||
    status === 'extraction_complete'
  ) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-secondary">
          <Loader2 size={24} className="animate-spin text-tertiary" />
          Reading the sheet and matching it to Odoo. Large sheets take a couple of minutes.
        </div>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle size={24} className="text-danger" />
          <p className="m-0 text-sm text-secondary">Extraction failed</p>
          {intake.error ? (
            <p className="m-0 max-w-md text-xs text-tertiary">{intake.error}</p>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={retry.isPending}
            onClick={() => retry.mutate({ intakeId, vendorId })}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  const effectiveDate = intake.effective_date
  const isFuture = effectiveDate != null && effectiveDate > localTodayIso()
  const missingUnit = lines.filter(needsPriceUnit)
  const changedCount = lines.filter(
    (l) => l.price_changed && ['matched', 'scheduled', 'applied'].includes(l.line_status),
  ).length
  const closed = status === 'applied' || status === 'rejected'

  function runApply(ids: number[]) {
    apply.mutate(
      { intakeId: intakeId!, lineIds: ids, vendorId },
      {
        onSuccess: (res) => {
          setSelection({})
          void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'items', vendorId] })
          const parts = []
          if (res.applied.length) parts.push(`${res.applied.length} written to Odoo`)
          if (res.scheduled.length)
            parts.push(`${res.scheduled.length} scheduled for ${formatIsoDate(res.effective_date)}`)
          if (res.skipped.length) parts.push(`${res.skipped.length} skipped`)
          toast.success(parts.join(' · ') || 'Nothing applied.')
          if (res.skipped.length)
            toast.error(
              res.skipped
                .map((s) => s.reason)
                .slice(0, 3)
                .join('; '),
            )
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Apply failed.'),
      },
    )
  }

  function confirmSelected() {
    acceptSuggested.mutate(
      { intakeId: intakeId!, lineIds: selConfirmable.map((l) => l.id) },
      {
        onSuccess: (res) => {
          setSelection({})
          toast.success(`${res.accepted.length} line(s) confirmed — now ready to apply.`)
          if (res.skipped.length) {
            toast.error(`${res.skipped.length} need a row picked: ${res.skipped[0]!.reason}`)
          }
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Confirm failed.'),
      },
    )
  }

  function unscheduleSelected() {
    const ids = selScheduled.map((l) => l.id)
    unschedule.mutate(
      { intakeId: intakeId!, lineIds: ids },
      {
        onSuccess: () => {
          setSelection({})
          toast.success(`${ids.length} line(s) unscheduled — back in review, nothing will change in Odoo.`)
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Unschedule failed.'),
      },
    )
  }

  function changeEffectiveDate(next: string | null) {
    const held = lines.filter((l) => l.line_status === 'scheduled').length
    if (
      held > 0 &&
      !window.confirm(
        `${held} scheduled line(s) will move to ${next ? formatIsoDate(next) : 'today (applied at once)'}. Continue?`,
      )
    ) {
      return
    }
    setEffectiveDate.mutate(
      { intakeId: intakeId!, effectiveDate: next },
      {
        onSuccess: (res) => {
          if (res?.rescheduled) toast.success(`${res.rescheduled} scheduled line(s) moved.`)
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not change the date.'),
      },
    )
  }

  async function skipSelected() {
    const ids = selSkippable.map((l) => l.id)
    try {
      for (const id of ids) {
        await patchLine.mutateAsync({ intakeId: intakeId!, lineId: id, line_status: 'skipped' })
      }
      setSelection({})
      toast.success(`${ids.length} line(s) skipped.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Skip failed.')
    }
  }

  const readyIds = lines
    .filter((l) => reviewGroup(l) === 'ready' && isApplicable(l))
    .map((l) => l.id)
  const verb = isFuture ? `Schedule for ${formatIsoDate(effectiveDate!)}` : 'Apply to Odoo'

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title={`Price sheet — ${vendorName}`}
        description={`${lines.length} line(s) extracted${intake.created_at ? ` · imported ${new Date(intake.created_at).toLocaleString()}` : ''}`}
      >
        <Button variant="ghost" size="sm" onClick={backToVendor}>
          <ArrowLeft size={12} /> Back to {vendorName}
        </Button>
        {closed ? (
          <Chip size="md" variant={status === 'applied' ? 'success' : 'neutral'} className="ml-2">
            {status === 'applied' ? 'Applied' : 'Rejected'}
          </Chip>
        ) : (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-secondary">
              Effective
              <input
                type="date"
                aria-label="Effective date"
                value={effectiveDate ?? ''}
                disabled={setEffectiveDate.isPending}
                onChange={(e) => changeEffectiveDate(e.target.value || null)}
                className="rounded-xs border border-border-default bg-surface-sunk px-2 py-1 text-sm"
              />
            </label>
            <a
              href={priceChangesCsvUrl(intakeId)}
              download
              className="inline-flex items-center gap-1.5 text-xs text-secondary hover:text-primary hover:underline"
            >
              <Download size={14} /> Export changes ({changedCount})
            </a>
            {showReject ? (
              <>
                <input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejecting…"
                  className="w-56 rounded-xs border border-border-default bg-surface-sunk px-2 py-1 text-sm"
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!rejectReason.trim() || reject.isPending}
                  onClick={() =>
                    reject.mutate(
                      { intakeId, reason: rejectReason.trim(), vendorId },
                      { onSuccess: backToVendor },
                    )
                  }
                >
                  Confirm reject
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowReject(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setShowReject(true)}>
                Reject import
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={readyIds.length === 0 || apply.isPending}
              onClick={() => runApply(readyIds)}
            >
              {verb}: all {readyIds.length} ready
            </Button>
          </div>
        )}
      </PageHeaderBand>

      <div className="flex flex-col gap-2 px-4 pt-3">
        {isFuture && !closed ? (
          <div className="rounded-xs border border-info-border bg-info-bg px-3 py-2 text-xs text-info-text">
            Prices take effect {formatIsoDate(effectiveDate!)}. Applied lines are scheduled, not
            written now — Odoo keeps the current prices until that date.
          </div>
        ) : null}
        <DroppedTiersCard
          intakeId={intakeId}
          vendorName={vendorName}
          rows={intake.dropped_rows ?? []}
          effectiveDate={effectiveDate}
        />
        {missingUnit.length > 0 && !closed ? (
          <div className="flex flex-wrap items-center gap-2 rounded-xs border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-text">
            <span className="flex-1">
              {missingUnit.length} line(s) don&apos;t say what unit the price is per. Prices on this
              sheet are per:
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={setPriceUnit.isPending}
              onClick={() => setPriceUnit.mutate({ intakeId, unit: 'kg' })}
            >
              kg
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={setPriceUnit.isPending}
              onClick={() => setPriceUnit.mutate({ intakeId, unit: 'lb' })}
            >
              lb
            </Button>
          </div>
        ) : null}
      </div>

      <Toolbar>
        <SearchInput
          containerClassName="flex-1 max-w-none"
          placeholder="Search vendor product, code or SPI product…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search lines"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Show lines">
            {FILTERS.map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? 'primary' : 'secondary'}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : GROUP_LABEL[f]} ({counts[f]})
              </Button>
            ))}
          </div>
        </ToolbarLeft>
        <ToolbarRight>
          <ToolbarCount>{`${visible.length} of ${lines.length}`}</ToolbarCount>
        </ToolbarRight>
      </Toolbar>

      {hasSelection ? (
        <div
          role="region"
          aria-label={`${selectedLines.length} selected — bulk actions`}
          className="sticky top-0 z-10 mx-4 mb-2 flex flex-wrap items-center gap-2 rounded-xs border border-border-default bg-bg-inverse px-3 py-2 text-text-inverse shadow-md"
        >
          <span className="text-sm font-semibold tabular-nums">{selectedLines.length} selected</span>
        {selApplicable.length > 0 ? (
          <Button
            variant="primary"
            size="sm"
            disabled={apply.isPending}
            onClick={() => runApply(selApplicable.map((l) => l.id))}
          >
            {isFuture ? 'Schedule' : 'Apply'} {selApplicable.length}
          </Button>
        ) : null}
        {selConfirmable.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={acceptSuggested.isPending}
            onClick={confirmSelected}
          >
            Confirm suggested row ({selConfirmable.length})
          </Button>
        ) : null}
        {selScheduled.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={unschedule.isPending}
            onClick={unscheduleSelected}
          >
            Unschedule {selScheduled.length}
          </Button>
        ) : null}
        {selSkippable.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={patchLine.isPending}
            onClick={() => void skipSelected()}
          >
            Skip {selSkippable.length}
          </Button>
        ) : null}
          <button
            type="button"
            className="ml-auto text-xs underline-offset-2 hover:underline"
            onClick={() => setSelection({})}
          >
            Clear selection <kbd className="ml-1 opacity-70">Esc</kbd>
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 px-4 pb-4">
        <DataTable
          data={visible}
          columns={columns}
          getRowId={(l) => String(l.id)}
          selection={selection}
          onSelectionChange={setSelection}
          onRowClick={(l) => toggleExpanded(String(l.id))}
          expandedRowIds={expanded}
          renderExpandedRow={(l) => (
            <PriceSheetLineResolver
              line={l}
              intakeId={intakeId}
              vendorName={vendorName}
              vendorItems={vendorItems}
              claimedBy={claimedBy}
            />
          )}
          moduleId="vendor-price-sheet-review"
          maxHeight="100%"
          minTableWidth={1100}
          emptyState={
            <EmptyState
              variant="no-data"
              title={
                search
                  ? 'No lines match that search'
                  : `No lines in “${filter === 'all' ? 'All' : GROUP_LABEL[filter]}”`
              }
            />
          }
        />
      </div>

      {status === 'applied' ? (
        <div className="flex items-center gap-2 px-4 pb-3 text-sm text-success">
          <CheckCircle2 size={16} /> Every line on this sheet is applied, scheduled or skipped.
        </div>
      ) : null}
    </div>
  )
}
